/**
 * Der Weg zur API.
 *
 * Eine Stelle, an der der Token sitzt, und eine Stelle, an der ein Fehler
 * entsteht. Jede Antwort, die nicht 2xx ist, wird zu einem Fehler mit
 * `code`: die Oberflaeche entscheidet danach und nicht nach Text, denn der
 * Text ist uebersetzt und der Code nicht.
 */
import type { ApiError, Session } from './types'

const BASE = '/api/v1'
const STORE = 'bsb.session'

let session: Session | null = null
let refreshing: Promise<void> | null = null

export function loadSession(): Session | null {
  if (session) return session
  try {
    const raw = localStorage.getItem(STORE)
    session = raw ? (JSON.parse(raw) as Session) : null
  } catch {
    session = null
  }
  return session
}

export function saveSession(next: Session | null): void {
  session = next
  try {
    if (next) localStorage.setItem(STORE, JSON.stringify(next))
    else localStorage.removeItem(STORE)
  } catch {
    /* Privater Modus: die Sitzung lebt dann nur im Speicher. */
  }
}

function fail(status: number, code: string, message: string, details?: Record<string, unknown>): ApiError {
  const e = new Error(message) as ApiError
  e.code = code
  e.status = status
  e.details = details
  return e
}

async function raw<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const s = loadSession()
  const headers = new Headers(init.headers)
  headers.set('accept', 'application/json')
  if (init.body) headers.set('content-type', 'application/json')
  if (s?.access_token) headers.set('authorization', `Bearer ${s.access_token}`)

  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, { ...init, headers })
  } catch {
    throw fail(0, 'offline', 'no connection')
  }

  if (res.status === 401 && retry) {
    if (s?.refresh_token) {
      // Einmal auffrischen, dann nochmal. Zweimal hiesse: die Kette ist weg.
      await refreshSession()
      return raw<T>(path, init, false)
    }
    // Gar keine Sitzung: die Startseite ruft Routen auf, die eine
    // brauchen, und ein Besucher kommt ohne. Hier entsteht sie, statt
    // dass der erste Knopf mit „unauthorized" antwortet.
    if (!path.startsWith('/auth/')) {
      await ensureSession()
      return raw<T>(path, init, false)
    }
  }
  if (res.status === 204) return undefined as T

  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    body = null
  }
  if (!res.ok) {
    const b = (body ?? {}) as { code?: string; message?: string; details?: Record<string, unknown> }
    throw fail(res.status, b.code ?? 'error', b.message ?? res.statusText, b.details)
  }
  return body as T
}

export async function refreshSession(): Promise<void> {
  if (refreshing) return refreshing
  const s = loadSession()
  if (!s?.refresh_token) return
  refreshing = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${s.access_token}` },
        body: JSON.stringify({ refresh_token: s.refresh_token, room_id: currentRoomId }),
      })
      if (!res.ok) {
        saveSession(null)
        return
      }
      saveSession((await res.json()) as Session)
    } finally {
      refreshing = null
    }
  })()
  return refreshing
}

let currentRoomId: string | null = null
export function setCurrentRoom(id: string | null): void {
  currentRoomId = id
}

export const api = {
  get: <T>(p: string) => raw<T>(p),
  post: <T>(p: string, body?: unknown) =>
    raw<T>(p, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(p: string, body?: unknown) =>
    raw<T>(p, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  del: <T>(p: string) => raw<T>(p, { method: 'DELETE' }),
}

export async function ensureSession(displayName?: string): Promise<Session> {
  const existing = loadSession()
  if (existing) return existing
  const fresh = await api.post<Session>('/auth/guest', {
    display_name: displayName,
    locale: navigator.language.startsWith('en') ? 'en' : 'de',
  })
  saveSession(fresh)
  return fresh
}

export async function mqttTokenFor(roomId: string, roles: string[]): Promise<{ mqtt_token: string; mqtt_url: string }> {
  return api.post('/auth/mqtt-token', { room_id: roomId, roles })
}
